import React, { useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell
} from "recharts";
import { Slider, useTheme } from "@mui/material";
import { subWeeks, format, startOfWeek } from "date-fns";

import { DebtHistoryCase } from "../Routes/CompanyAssessment/types";
import { useTranslation } from 'react-i18next';
import { formatNumber as formatLocalizedNumber } from '../i18n/formatters';
import { getTimeSeriesTooltipStyles, TIME_SERIES_CHART_HEIGHT, TIME_SERIES_CHART_MARGIN, TIME_SERIES_COLORS, TIME_SERIES_TEXT_STYLE } from './timeSeriesChartStyles';

interface DebtCase {
  date: string;
  type: "private" | "public";
  status: string;
  authority: string;
  debt: string;
}

interface Props {
  data: DebtHistoryCase[];
  debtCases: DebtCase[];
}


const colorMap = {
  public: TIME_SERIES_COLORS.green,
  privateAnsokan: TIME_SERIES_COLORS.purple,
  privateUtslag: TIME_SERIES_COLORS.slate,
};

const TimeSeriesChart: React.FC<Props> = ({ data, debtCases }) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const tooltipStyles = getTimeSeriesTooltipStyles(theme);
  const [monthsBack, setMonthsBack] = useState(12); // Default to 12 months

  const handleSliderChange = (event: Event, newValue: number | number[]) => {
    setMonthsBack(newValue as number);
  };

  // Generate the last 12 months' weekly dates
  const now = new Date();
  const weeklyDates = Array.from({ length: monthsBack * 4 }, (_, i) =>
    format(startOfWeek(subWeeks(now, i)), "yyyy-MM-dd")
  ).reverse();

  // Filter cases based on the selected monthsBack
  const filterDate = new Date();
  filterDate.setMonth(filterDate.getMonth() - monthsBack);

  const filteredDebtCases = debtCases.filter((caseItem) => {
    const caseDate = new Date(caseItem.date);
    return caseDate >= filterDate;
  });

  // Prepare data for the stacked bar chart - each case gets its own bar
  const updatedStackedBarData = filteredDebtCases.map((caseItem, index) => {
    const date = format(new Date(caseItem.date), "yyyy-MM-dd");
    
    // Determine chart type based on type and status
    let chartType: string;
    if (caseItem.type === "private") {
      if (caseItem.status === "Utslag") {
        chartType = "privateUtslag";
      } else if (caseItem.status === "Ansökan") {
        chartType = "privateAnsokan";
      } else {
        chartType = "public"; // Payment orders without specific status - use green color
      }
    } else {
      // Public debt cases - use the "public" chart type (green color)
      chartType = "public";
    }
    
    return {
      Date: `${date}-${index}`, // Add index to make each entry unique
      DisplayDate: date, // Keep original date for display
      Debt: Number(caseItem.debt),
      Authority: caseItem.authority,
      Type: chartType,
      Status: caseItem.status || t(caseItem.type === "public" ? 'assessment.details.publicDebt' : 'assessment.details.paymentOrder')
    };
  });  // Sort the updatedStackedBarData by date in chronological order
  const sortedUpdatedStackedBarData = updatedStackedBarData.sort(
    (a, b) => new Date(a.DisplayDate).getTime() - new Date(b.DisplayDate).getTime()
  );

  // Filter the debt trend data based on monthsBack
  const filteredChartData = data.filter((item) => {
    const itemDate = new Date(item.date);
    return itemDate >= filterDate;
  });

    // Map the data to the weekly dates, aligning data dates to the nearest week
    const chartData = weeklyDates.map((date) => {
      const matchingData = filteredChartData.find(
        (item) =>
          format(startOfWeek(new Date(item.date)), "yyyy-MM-dd") === date
      );
  
      if (!matchingData) {
        return {
          Date: date,
          AMAL: null,
          EMAL: null,
          Total: null,
        };
      }
  
      return {
        Date: date,
        AMAL: matchingData.amalDebt > 0 ? Number(matchingData.amalDebt) : null,
        EMAL: matchingData.emalDebt > 0 ? Number(matchingData.emalDebt) : null,
        Total: matchingData.totalDebt > 0 ? Number(matchingData.totalDebt) : null,
      };
    });




  const formatNumber = (num: number) => formatLocalizedNumber(num, i18n.resolvedLanguage);

  const textStyle = TIME_SERIES_TEXT_STYLE;

  // Find the maximum debt value to set the Y-axis domain
  const maxDebt = Math.max(
    ...chartData.map(item => Math.max(item.Total || 0)) 
  );

  const roundedMaxDebt = Math.ceil((maxDebt * 1.1) / 100000) * 100000;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
      {/* Slider for selecting months back */}
      <div style={{ marginBottom: "0px", textAlign: "center" }}>
        <label htmlFor="months-back-slider" style={{ display: "block", fontWeight: "bold", marginBottom: "0px", fontSize: "12px" }}>
          {t('assessment.details.selectTimeRange')}
        </label>
        <p style={{ marginBottom: "0px", fontSize: "10px", color: "gray" }}>
          {t('assessment.details.timeRangeHelp')}
        </p>
        <Slider
          id="months-back-slider"
          value={monthsBack}
          onChange={handleSliderChange}
          aria-labelledby="months-back-slider"
          min={0}
          max={36}
          size="small"
          valueLabelDisplay="auto"
        />
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: "10px", color: "gray", marginTop: "-10px" }}>
          <span>0</span>
          <span>36</span>
        </div>
        <p style={{ fontSize: "10px", color: "gray", marginTop: "0px" }}>{t('assessment.details.showingMonths', { count: monthsBack })}</p>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
        {/* Stacked Bar Chart for Private and Public Debt Cases */}
        <div style={{ width: "100%", textAlign: "center" }}>
          <h3 style={{ fontSize: "12px", fontWeight: "bold", margin: "5px 0" }}>{t('assessment.details.paymentOrders')}</h3>
          <ResponsiveContainer width="100%" height={TIME_SERIES_CHART_HEIGHT}>
            <BarChart
              data={sortedUpdatedStackedBarData}
              margin={TIME_SERIES_CHART_MARGIN}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="DisplayDate" style={textStyle} />
              <YAxis tickFormatter={formatNumber} style={textStyle} />
              <Tooltip
                {...tooltipStyles}
                formatter={(value, name, props) => [
                  typeof value === "number" ? formatNumber(value) : value,
                  `${props.payload.Authority} (${props.payload.Status})` // Show authority name and status on hover
                ]}
              />
              <Legend
                payload={[
                  { value: t('assessment.details.publicDebtCases'), type: "square", color: colorMap.public },
                  { value: t('assessment.details.privateApplications'), type: "square", color: colorMap.privateAnsokan },
                  { value: t('assessment.details.privateDecisions'), type: "square", color: colorMap.privateUtslag }
                ]}
                wrapperStyle={textStyle}
              />
              <Bar dataKey="Debt" name={t('personSearch.debt')}>
                {sortedUpdatedStackedBarData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={colorMap[entry.Type as keyof typeof colorMap]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Debt Trend Line Chart */}
        <div style={{ width: "100%", textAlign: "center" }}>
          <h3 style={{ fontSize: "12px", fontWeight: "bold", margin: "5px 0" }}>{t('assessment.details.kfmDebtTrend')}</h3>
          <ResponsiveContainer width="100%" height={TIME_SERIES_CHART_HEIGHT}>
            <LineChart
              data={chartData}
              margin={TIME_SERIES_CHART_MARGIN}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="Date" style={textStyle} />
              <YAxis tickFormatter={formatNumber} style={textStyle} domain={[0, roundedMaxDebt]} // Set domain to 10% above the maximum debt value
                allowDataOverflow={true} // Allow data overflow
              />
              <Tooltip {...tooltipStyles} formatter={formatNumber} />
              <Legend wrapperStyle={textStyle} />
              <Line
                type="monotone"
                dataKey="AMAL"
                name={t('assessment.debt.public')}
                stroke={colorMap.public}
                connectNulls={false}
                isAnimationActive={false}
              />
              <Line
                type="monotone"
                dataKey="EMAL"
                name={t('assessment.debt.private')}
                stroke={colorMap.privateAnsokan}
                connectNulls={false}
                isAnimationActive={false}
              />
              <Line
                type="monotone"
                dataKey="Total"
                name={t('assessment.debt.total')}
                stroke={colorMap.privateUtslag}
                connectNulls={false}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default TimeSeriesChart;
